import { type ViewerCaption, type ViewerImage, type ViewerVideo } from '@/components/media';
import {
  type DeliveryCaption,
  type DeliveryGalleryItem,
  type DeliveryMedia,
} from '@/services/deliveries';

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const;

/**
 * `'2026-10-04'` → `'October 2026'`.
 *
 * Parsed by hand rather than through `Date`. `new Date('2026-10-04')` is
 * parsed as UTC midnight and then printed in the reader's own zone, so west
 * of Greenwich this date is September — and the site is prerendered in CI,
 * which means the HTML and the hydrated render could disagree about the month
 * and React would replace it. The same reason `formatClockTime` is pure.
 *
 * The day is deliberately dropped. "October 2026" is the honest resolution for
 * an archive: it dates the handover without turning a customer's week into
 * public record, and it does not go stale the way "3 days ago" does on a page
 * that is prerendered once a week.
 */
export function deliveryMonth(iso: string): string {
  const [year, month] = iso.split('-');
  const index = Number(month) - 1;
  const name = MONTHS[index];
  return name && year ? `${name} ${year}` : iso;
}

/** The one supporting line under a title: 'Kampala, Uganda · October 2026'. */
export function deliveryMeta(delivery: DeliveryCaption): string {
  return `${delivery.destination} · ${deliveryMonth(delivery.deliveredOn)}`;
}

/**
 * What a screen reader is told about one photograph or clip.
 *
 * The stored `alt` wins when there is one. Without it the sentence is built
 * from the handover, because the alternative in a gallery is silence: these
 * pictures illustrate no copy — they *are* the content — so the `alt=""` that
 * is right for a decorative photograph beside a paragraph is exactly wrong
 * here.
 *
 * `showsClient` changes the verb and nothing else. "handed over to its owner"
 * says what the picture proves; "photographed on delivery" says what the other
 * kind of picture proves. Guessing the first for every entry would put a
 * person into a description of an empty car park.
 */
export function deliveryAltText(delivery: DeliveryCaption, media: DeliveryMedia): string {
  if (media.alt) return media.alt;

  const subject = `${delivery.vehicle}, ${delivery.destination}, ${deliveryMonth(delivery.deliveredOn)}`;
  if (media.kind === 'video') return `Video of a ${subject}`;

  return delivery.showsClient
    ? `${subject}, handed over to its owner`
    : `${subject}, photographed on delivery`;
}

/** The accessible name of a clip's player. */
export function deliveryVideoLabel(delivery: DeliveryCaption): string {
  return `${delivery.vehicle} delivered to ${delivery.destination}`;
}

/**
 * The words that travel with a medium into either viewer.
 *
 * The call to action is the same on every item and points at the one thing
 * somebody looking at other people's cars might next want: their own
 * quotation. It is passed in rather than written here so a caller on the
 * vehicle page could point somewhere more specific.
 */
export function deliveryViewerCaption(
  delivery: DeliveryCaption,
  action?: { label: string; to: string },
): ViewerCaption {
  return {
    title: delivery.vehicle,
    meta: deliveryMeta(delivery),
    quote: delivery.testimonial
      ? { text: delivery.testimonial.quote, attribution: delivery.testimonial.attribution }
      : null,
    action: action ?? null,
  };
}

/** One gallery item as the lightbox reads it. */
export function toViewerImage(
  item: DeliveryGalleryItem,
  action?: { label: string; to: string },
): ViewerImage {
  return {
    id: item.media.id,
    src: item.media.src,
    alt: deliveryAltText(item.delivery, item.media),
    width: item.media.width,
    height: item.media.height,
    caption: deliveryViewerCaption(item.delivery, action),
  };
}

/** One gallery item as the reel reads it. */
export function toViewerVideo(
  item: DeliveryGalleryItem,
  action?: { label: string; to: string },
): ViewerVideo {
  return {
    id: item.media.id,
    src: item.media.src,
    poster: item.media.poster,
    label: deliveryVideoLabel(item.delivery),
    orientation: item.media.orientation,
    durationSec: item.media.durationSec,
    caption: deliveryViewerCaption(item.delivery, action),
  };
}

/** `6` → `'0:06'`. For the badge on a clip's tile. */
export function formatClipDuration(seconds: number): string {
  const whole = Math.max(0, Math.round(seconds));
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, '0')}`;
}
